import {
  getIndexByCode,
  getLastPageWithUnansweredQuestions,
  getLastSectionWithUnansweredQuestions,
  getSectionsByPageCode,
} from "..//utils";

import { CurrentPageAndSection } from "../types/CurrentPageAndSection";
import { EdhConditionDisclosureResource } from "../types/EdhConditionDisclosureResource";
import { QuestionPageResource } from "../types/QuestionPageResource";
import { QuestionSectionResource } from "../types/QuestionSectionResource";
import { getLastElementInArray } from "../../../utils/generalUtils";
import { pageHasVisibleQuestions } from "../validation/page";
import { sectionHasVisibleQuestions } from "../validation/section";

/**
 * Find the index of current page in pages array.
 * @param pages List of pages.
 * @param currentPage Code of current page.
 */
export const getCurrentPageIndex = (
  pages: QuestionPageResource[],
  currentPage: string,
) => {
  return pages.findIndex(({ code }) => code === currentPage);
};

/**
 * Gets the index of section passed in.
 * @param sections List of sections in interview.
 * @param currentSection Sections to get index of.
 */
export const getIndexOfSection = (
  sections: QuestionSectionResource[],
  currentSection: string,
) => {
  return sections.findIndex(({ code }) => code === currentSection);
};

/**
 * Gets the code of the first section in provided page.
 * @param pages List of pages in.
 * @param pageCode Page code to get sections from.
 */
export const updatePageAndFirstSectionByPageCode = (
  pages: QuestionPageResource[],
  pageCode: string,
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  const pageIndex = getCurrentPageIndex(pages, pageCode);
  const pageSections = getSectionsByPageCode(pages, pageCode);
  const firstSection = pages[pageIndex].sections[0];

  if (pageSections) {
    const section = getLastSectionWithUnansweredQuestions(
      pageSections,
      embeddedDisclosures,
    );
    if (section) {
      const sectionIndex = getIndexByCode(pageSections, section.code);
      return {
        page: { code: pageCode, index: pageIndex },
        section: { code: section.code, index: sectionIndex },
      };
    }
  }

  // No sections with unanswered questions found. Set first section as current.
  return {
    page: {
      code: pageCode,
      index: pageIndex,
    },
    section: {
      code: firstSection.code,
      index: 0,
    },
  };
};

/**
 * Gets the last page and section containing unanswered questions.
 * If interview is complete, returns the last page and section.
 * @param pages List of interview pages.
 * @param current Current page and section from state.
 */
export const findLastActivePageAndSection = (
  pages: QuestionPageResource[],
  current: CurrentPageAndSection,
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  const next = Object.assign({}, current);

  // Check if we have any pages with unanswered question.
  const lastUnansweredPage = getLastPageWithUnansweredQuestions(
    pages,
    embeddedDisclosures,
  );

  if (lastUnansweredPage !== undefined) {
    next.page.code = lastUnansweredPage.code;
    next.page.index = getIndexByCode(pages, lastUnansweredPage.code);
  } else {
    const lastPage = getLastElementInArray(pages);
    const pageIndex = getIndexByCode(pages, lastPage.code);
    if (lastPage) {
      next.page = { code: lastPage.code, index: pageIndex };
    }
  }

  if (next.page) {
    const pageSections = getSectionsByPageCode(pages, next.page.code);

    if (pageSections) {
      const lastActiveSection = getLastSectionWithUnansweredQuestions(
        pageSections,
        embeddedDisclosures,
      );

      if (lastActiveSection) {
        next.section.code = lastActiveSection.code;
        next.section.index = getIndexByCode(
          pageSections,
          lastActiveSection.code,
        );
      } else {
        const { code } = getLastElementInArray(pageSections);
        const sectionIndex = getIndexOfSection(pageSections, code);
        next.section = { code: code, index: sectionIndex };
      }
    }
  }

  return next;
};

/**
 * Gets next section and page in interview.
 * @param pages List of interview pages from state.
 * @param current Current page and section of interview.
 */
export const getNextSectionForCurrent = (
  pages: QuestionPageResource[],
  current: CurrentPageAndSection,
) => {
  const next = Object.assign({}, current);
  const visiblePages = pages.filter((page) => pageHasVisibleQuestions(page));
  const currentPageIndex = getIndexByCode(visiblePages, current.page.code);
  const visibleSections = visiblePages[currentPageIndex].sections.filter(
    (section) => sectionHasVisibleQuestions(section),
  );
  const currentSectionIndex = getIndexByCode(
    visibleSections,
    current.section.code,
  );
  const nextSectionIndex = currentSectionIndex + 1;

  if (nextSectionIndex <= visibleSections.length - 1) {
    const nextSection = visibleSections[nextSectionIndex].code;

    if (nextSection) {
      next.section.code = nextSection;
    }
  } else if (currentPageIndex < visiblePages.length) {
    const nextPage = visiblePages[currentPageIndex + 1];
    if (nextPage) {
      next.page.code = nextPage.code;
      // filter to make sure the next section has visible questions
      next.section.code = nextPage.sections.filter((section) =>
        sectionHasVisibleQuestions(section),
      )[0].code;
    }
  }
  return next;
};

/**
 * Gets previous section and page in interview.
 * @param pages List of interview pages from state.
 * @param current Current page and section of interview.
 */
export const getPreviousSectionForCurrent = (
  pages: QuestionPageResource[],
  current: CurrentPageAndSection,
) => {
  const next = Object.assign({}, current);
  const visiblePages = pages.filter((page) => pageHasVisibleQuestions(page));
  const currentPageIndex = getIndexByCode(visiblePages, current.page.code);
  const visibleSections = visiblePages[currentPageIndex].sections.filter(
    (section) => sectionHasVisibleQuestions(section),
  );
  const currentSectionIndex = getIndexByCode(
    visibleSections,
    current.section.code,
  );

  if (currentSectionIndex - 1 >= 0) {
    const previousSection = visibleSections[currentSectionIndex - 1].code;
    if (previousSection) {
      next.section.code = previousSection;
      next.section.index = getIndexByCode(visibleSections, previousSection);
    }
  }
  if (currentSectionIndex === 0) {
    if (currentPageIndex > 0) {
      const previousPage = visiblePages[currentPageIndex - 1];
      const previousPageVisibleSections = previousPage.sections.filter(
        (section) => sectionHasVisibleQuestions(section),
      );

      const previousSection = getLastElementInArray(
        previousPageVisibleSections,
      );

      if (previousPage) {
        next.page.code = previousPage.code;
      }

      if (previousSection) {
        next.section.code = previousSection.code;
        next.section.index = getIndexByCode(
          previousPageVisibleSections,
          previousSection.code,
        );
      }
    }
  }
  return next;
};
